// "Tender pipeline that day" table in the Admin cost dialog: the lots the
// pipeline summarised on a day, each with its publication's document set.
// This module owns the sorting; the JSX only renders.

export interface PipelineLot {
    tender_id: string;
    project_number: string | null;
    project_title: string | null;
    lot_number: number | null;
    lot_title: string | null;
    total_lots: number | null;
    documents: number | null;   // files in the publication's document set (shared by its lots)
    parts: number | null;       // document parts given metadata = LLM calls for that set
}

export type PipelineSortKey = 'number' | 'documents' | 'parts';

export interface PipelineSort {
    key: PipelineSortKey;
    dir: 'asc' | 'desc';
}

// Biggest document sets first: that is where the day's money went.
export const DEFAULT_PIPELINE_SORT: PipelineSort = { key: 'documents', dir: 'desc' };

// Natural first direction of a column: counts high first, numbers low first.
export const naturalDirection = (key: PipelineSortKey): 'asc' | 'desc' => (key === 'number' ? 'asc' : 'desc');

// Clicking a header: the same key flips the direction, a new key starts
// with its natural direction.
export function nextPipelineSort(current: PipelineSort, key: PipelineSortKey): PipelineSort {
    if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' };
    return { key, dir: naturalDirection(key) };
}

const numberValue = (text: string | null): number | null => {
    if (!text) return null;
    const n = Number(text);
    return Number.isFinite(n) ? n : null;
};

const compareText = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0);

// Sort by the chosen column; unknown values go last whatever the direction.
// Ties keep the SQL order: project number, lot number, tender id.
export function sortPipelineLots(lots: PipelineLot[], sort: PipelineSort): PipelineLot[] {
    const sign = sort.dir === 'asc' ? 1 : -1;
    const value = (lot: PipelineLot): number | null =>
        sort.key === 'documents' ? lot.documents
        : sort.key === 'parts' ? lot.parts
        : numberValue(lot.project_number);
    return [...lots].sort((a, b) => {
        const va = value(a);
        const vb = value(b);
        if (va == null && vb != null) return 1;
        if (va != null && vb == null) return -1;
        if (va != null && vb != null && va !== vb) return sign * (va - vb);
        const na = numberValue(a.project_number);
        const nb = numberValue(b.project_number);
        if (na != null && nb != null && na !== nb) return na - nb;
        const byText = compareText(a.project_number ?? '', b.project_number ?? '');
        if (byText !== 0) return byText;
        const byLot = (a.lot_number ?? 0) - (b.lot_number ?? 0);
        if (byLot !== 0) return byLot;
        return compareText(a.tender_id, b.tender_id);
    });
}
